import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Box, Tooltip, useTheme } from '@mui/material';
import { stateLegislatorUrls } from '../utils/stateLegislatorUrls';

const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

interface USStateMapProps {
  darkMode?: boolean;
  onStateClick?: (stateName: string, url: string) => void;
}

export const USStateMap: React.FC<USStateMapProps> = ({ darkMode, onStateClick }) => {
  const theme = useTheme();
  const [hoveredState, setHoveredState] = useState<string | null>(null);

  const handleGeographyClick = (geo: any) => {
    const stateName = geo.properties.name;
    const stateData = stateLegislatorUrls.find((s) => s.state.toLowerCase() === stateName.toLowerCase());

    if (stateData) {
      // Call optional callback first
      if (onStateClick) {
        onStateClick(stateName, stateData.stateUrlFindLeg);
      }

      // Open legislator lookup URL in new tab with security attributes
      window.open(stateData.stateUrlFindLeg, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 900,
        margin: '0 auto',
        '& svg': {
          width: '100%',
          height: 'auto',
        },
      }}
    >
      <ComposableMap projection="geoAlbersUsa">
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const isHovered = hoveredState === geo.properties.name;
              const fillColor = isHovered ? theme.palette.primary.main : darkMode ? '#444' : '#D6D6DA';

              return (
                <Tooltip key={geo.rsmKey} title={geo.properties.name} placement="top" arrow>
                  <g
                    onMouseEnter={() => setHoveredState(geo.properties.name)}
                    onMouseLeave={() => setHoveredState(null)}
                    onClick={() => handleGeographyClick(geo)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Geography
                      geography={geo}
                      fill={fillColor}
                      stroke="#FFF"
                      strokeWidth={0.5}
                      style={{
                        default: {
                          outline: 'none',
                          transition: 'fill 0.2s ease-in-out',
                        },
                        hover: {
                          fill: theme.palette.primary.main,
                          outline: 'none',
                        },
                        pressed: {
                          fill: theme.palette.primary.dark,
                          outline: 'none',
                        },
                      }}
                    />
                  </g>
                </Tooltip>
              );
            })
          }
        </Geographies>
      </ComposableMap>
    </Box>
  );
};
